import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useProjectTreeStore } from 'store/projectContract.store';

import { FormProps } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/UpdateRiskRegisterModal/components/UpdateRiskRegisterForm/UpdateRiskRegisterForm';

export const useUpdateRiskRegisterFormLogic = ({ api, ui, handleModalClose }) => {
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();
    const { project, contract } = useProjectTreeStore();
    const { sendEventWithDimensions } = useEventWithDimensions();

    const form = useForm<FormProps>({
        values: api.getRisk.data,
    });

    const handleFormSubmit = (data: FormProps) => {
        sendEventWithDimensions({ action: 'Update Risk', label: data.name, category: 'Risk Register' });
        const filters: { data: unknown[] | null; name: string }[] = [
            { data: [project?.id], name: 'PROJECT_CARD_BOARD' },
            { data: [contract?.id], name: 'CONTRACT_CARD_BOARD' },
        ];
        if (data.linkOption === 'wbs') {
            filters.push({
                data: [data.activitiesByKey.wbs],
                name: 'WBS_SEARCH',
            });
        }
        if (data.linkOption === 'activityCodes') {
            filters.push({
                data: data.activitiesByKey.activityCodes,
                name: 'ACTIVITY_CODES',
            });
        }
        if (data.linkOption === 'cluster') {
            filters.push({
                data: [data.activitiesByKey.cluster],
                name: 'CLUSTER_CARD_BOARD',
            });
        }
        if (data.linkOption === 'specific') {
            filters.push({
                data: data.activitiesByKey.specific,
                name: 'CUSTOM_ACTIVITIES_SET',
            });
        }

        const segmentId =
            data.linkOption === 'segment' && data.activitiesByKey?.segment ? data.activitiesByKey.segment : null;

        const { activitiesByKey, assigneeValue, linkedActivities, linkOption, ...payload } = data;

        api.updateRisk.instance.mutate(
            {
                ...payload,
                assignee: data.assigneeValue?.value || '',
                segmentRequest: {
                    filter: {
                        filters,
                    },
                    segmentId,
                },
            },
            {
                onSuccess: () => {
                    Promise.all([
                        queryClient.invalidateQueries('getRiskRegister'),
                        queryClient.invalidateQueries('getRisk'),
                    ]).then(() => {
                        handleSnackBar('Risk register updated successfully', 'success');
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    ui.setters.setShowConfirmModal(false);
                },
            },
        );
    };

    const closeModalConfirm = () => {
        if (form.formState.isDirty || Object.keys(form.formState.dirtyFields).length > 0) {
            ui.setters.setShowConfirmModal(true);
        } else {
            handleModalClose();
        }
        return false;
    };

    return { logic: { form, handleFormSubmit, closeModalConfirm } };
};
